import { Navigate } from "react-router-dom";
import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import mcbLogo from "../../images/mcb-logo.png";
import {
  LayoutDashboard,
  Ship,
  ClipboardList,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Tag,
} from "lucide-react";

const NAV_ICONS = {
  Dashboard: LayoutDashboard,
  Sailings: Ship,
  Bookings: ClipboardList,
  Customers: Users,
  Livestock: Tag,
  Settings: Settings,
};

const getTopNav = (roles = []) => {
  const isAdmin = roles.includes("admin");
  const isOperations = roles.includes("operations");
  const isCommercial = roles.includes("commercial");

  const items = [{ label: "Dashboard", to: "/dashboard" }];

  if (isAdmin || isOperations)
    items.push({ label: "Sailings", to: "/dashboard/sailings" });

  if (isAdmin || isOperations || isCommercial)
    items.push({ label: "Bookings", to: "/dashboard/bookings" });

  if (isAdmin || isCommercial || isOperations)
    items.push({ label: "Customers", to: "/dashboard/customers" });

  if (isAdmin || isOperations)
    items.push({ label: "Livestock", to: "/dashboard/livestock" });

  return items;
};

const DashboardLayout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const roles = user.roles || [];
  const isAdmin = roles.includes("admin");
  const username = user.firstname || user.email || "User";

  const bottomNav = isAdmin
    ? [{ label: "Settings", to: "/dashboard/settings" }]
    : [];

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <aside
        className={`h-screen border-r border-gray-200 bg-white flex flex-col transition-all duration-200 ${
          isCollapsed ? "w-[64px]" : "w-[240px]"
        }`}
      >
        {/* Header */}
        <div className="h-16 px-3 border-b border-gray-200 flex items-center justify-between shrink-0">
          {!isCollapsed && (
            <Link to="/dashboard" className="flex flex-1 min-w-0 items-center justify-center">
              <img src={mcbLogo} alt="McCallum Bros Ltd" className="h-8 w-auto max-w-full" />
            </Link>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition ${
              isCollapsed ? "mx-auto" : "ml-auto"
            }`}
          >
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Main Nav */}
        <div className="flex-1 min-h-0 overflow-y-auto py-3 px-2 space-y-1">
          {getTopNav(roles).map((item) => {
            const Icon = NAV_ICONS[item.label] || LayoutDashboard;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/dashboard"}
                title={item.label}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-slate-950 text-white"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  } ${isCollapsed ? "justify-center" : ""}`
                }
              >
                <Icon size={18} className="shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </div>

        {/* Bottom Nav */}
        <div className="shrink-0 border-t border-gray-200 py-3 px-2 space-y-1">
          {bottomNav.map((item) => {
            const Icon = NAV_ICONS[item.label] || Settings;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                title={item.label}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-slate-950 text-white"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  } ${isCollapsed ? "justify-center" : ""}`
                }
              >
                <Icon size={18} className="shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}

          <button
            onClick={handleLogout}
            title={`Log out (${username})`}
            className={`w-full flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition ${
              isCollapsed ? "justify-center" : ""
            }`}
          >
            <LogOut size={18} className="shrink-0" />
            {!isCollapsed && <span>Log out ({username})</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 h-screen overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default DashboardLayout;